import { gatheringActivities, type GatheringActivity } from "@hanjan-shieo/content";

export type GatheringMood = "quiet" | "balanced";
export type GatheringPlan = { partySize: number; mood: GatheringMood; leaveBy: string };
export type GatheringStage = "activity" | "rest" | "leave" | "ended";
export type GatheringConductorState = { plan: GatheringPlan; stage: GatheringStage; completedActivityIds: string[]; restsCompleted: number };
export type GatheringConductorEvent =
  | { type: "COMPLETE_ACTIVITY"; activityId: string }
  | { type: "TAKE_REST" }
  | { type: "RESUME" }
  | { type: "CHECK_LEAVE" }
  | { type: "END" };

const VALID_ACTIVITY_IDS = new Set(gatheringActivities.map((activity) => activity.id));

export function normalizeGatheringPlan(input: GatheringPlan): GatheringPlan {
  return {
    partySize: Math.min(12, Math.max(2, Math.round(Number.isFinite(input.partySize) ? input.partySize : 4))),
    mood: input.mood === "quiet" ? "quiet" : "balanced",
    leaveBy: /^([01]\d|2[0-3]):[0-5]\d$/.test(input.leaveBy) ? input.leaveBy : "22:30",
  };
}

export function createGatheringConductor(plan: GatheringPlan): GatheringConductorState {
  return { plan: normalizeGatheringPlan(plan), stage: "activity", completedActivityIds: [], restsCompleted: 0 };
}

export function gatheringConductorReducer(state: GatheringConductorState, event: GatheringConductorEvent): GatheringConductorState {
  if (state.stage === "ended") return state;
  switch (event.type) {
    case "COMPLETE_ACTIVITY":
      if (!VALID_ACTIVITY_IDS.has(event.activityId)) return state;
      return { ...state, completedActivityIds: [...new Set([...state.completedActivityIds, event.activityId])] };
    case "TAKE_REST": return state.stage === "activity" ? { ...state, stage: "rest" } : state;
    case "RESUME": return state.stage === "rest" ? { ...state, stage: "activity", restsCompleted: state.restsCompleted + 1 } : state;
    case "CHECK_LEAVE": return state.stage === "activity" || state.stage === "rest" ? { ...state, stage: "leave" } : state;
    case "END": return state.stage === "leave" ? { ...state, stage: "ended" } : state;
  }
}

export function nextUncompletedActivity(activities: GatheringActivity[], state: GatheringConductorState): GatheringActivity | undefined {
  return activities.find((activity) => !state.completedActivityIds.includes(activity.id)) ?? activities[0];
}
